/**
 * canvas-engine/presets/decompositionAdapters/familyLayout.js
 *
 * Shared, family-specific layout tweaks used by the family adapters
 * (education / waas / flexOffice / retail). Kept tiny and deterministic: every
 * tweak is a pure transform over the normalized Canvas_Model produced by the
 * core `buildSectionCanvas` engine.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules
 * (`./buildSectionCanvas`). No React, no DOM, no side effects. Never emits a
 * `native` element (it only repositions elements the core engine produced).
 *
 * The one tweak implemented here is the faithful "hero / split" arrangement:
 * when the core engine stacks a lead text block directly above a single
 * full-width foreground image, family heroes/abouts/details look more faithful
 * with the image placed BESIDE the text (two columns) instead of below it. We
 * detect that exact stacked pattern and rearrange the two blocks side-by-side,
 * compacting everything that followed the image upward and shrinking the frame
 * height to match — so the result stays a valid, deterministic Canvas_Model.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
 */

import buildSectionCanvas, { DEFAULT_STACK_GAP } from './buildSectionCanvas';
import { MIN_DIMENSION } from '../../model/schema';

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Rearrange a stacked [lead text, full-width image] pair into a side-by-side
 * two-column row. Returns a NEW Canvas_Model; the input is never mutated.
 *
 * The transform only fires when ALL of these hold (otherwise the canvas is
 * returned unchanged):
 *   - there is no full-frame background image (a `*-bg-*` element) — a
 *     full-bleed hero is faithful stacked, so we leave it alone;
 *   - the first element is a `text` element (the lead block);
 *   - the element IMMEDIATELY after it is an `image` element stacked below it
 *     (image.y > text.y).
 *
 * @param {object} canvas a normalized Canvas_Model
 * @param {number} [gap] the horizontal gap between the two columns (px)
 * @returns {object} a new Canvas_Model
 */
export function splitLeadImageBeside(canvas, gap = DEFAULT_STACK_GAP) {
  if (!isPlainObject(canvas) || !Array.isArray(canvas.elements)) return canvas;

  const els = canvas.elements;
  // A full-bleed background image reads best stacked — skip the split.
  if (els.some((e) => typeof e.id === 'string' && e.id.includes('-bg-'))) return canvas;

  const leadIdx = els.findIndex((e) => e.type === 'text');
  if (leadIdx === -1) return canvas;

  const imgIdx = leadIdx + 1;
  const text = els[leadIdx];
  const image = els[imgIdx];
  if (!image || image.type !== 'image') return canvas;
  if (!(image.y > text.y)) return canvas; // must currently be stacked below

  // Work on a deep clone so the transform is pure.
  const next = JSON.parse(JSON.stringify(canvas));
  const nText = next.elements[leadIdx];
  const nImage = next.elements[imgIdx];

  const rowX = nText.x;
  const rowWidth = nText.width; // = content width at the reference frame
  const colWidth = Math.max((rowWidth - gap) / 2, MIN_DIMENSION);
  const rowTop = nText.y;

  const oldImageBottom = nImage.y + nImage.height;

  // Left column: lead text. Right column: the image, top-aligned with the text.
  nText.x = rowX;
  nText.y = rowTop;
  nText.width = colWidth;

  nImage.x = rowX + colWidth + gap;
  nImage.y = rowTop;
  nImage.width = colWidth;

  const rowHeight = Math.max(nText.height, nImage.height);
  const newRowBottom = rowTop + rowHeight;
  const delta = newRowBottom - oldImageBottom; // <= 0: everything after moves up

  if (delta !== 0) {
    for (let i = imgIdx + 1; i < next.elements.length; i += 1) {
      next.elements[i].y += delta;
    }
    next.height = Math.max(next.height + delta, MIN_DIMENSION);
  }

  return next;
}

/**
 * Build a normalized Canvas_Model for a family section, applying the
 * hero/split tweak where the descriptor opts in.
 *
 * @param {object} sectionData a transcribed preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options
 * @param {object} [hooks] `{ split?: boolean }` family layout hooks
 * @returns {object} a normalized Canvas_Model
 */
export function buildFamilyCanvas(sectionData, options = {}, hooks = {}) {
  const canvas = buildSectionCanvas(sectionData, options);
  if (hooks && hooks.split) {
    return splitLeadImageBeside(canvas);
  }
  return canvas;
}

/**
 * Decide whether a family descriptor should use the side-by-side split layout:
 * a two-column descriptor (`layout.columns === 2`) carrying exactly one
 * foreground image and no full-frame background image. This is the look the
 * Default_Source uses for split heroes, "about", and detail sections.
 *
 * @param {object} sectionData a transcribed preset DATA descriptor
 * @returns {boolean}
 */
export function wantsSplit(sectionData) {
  if (!isPlainObject(sectionData)) return false;
  const layout = isPlainObject(sectionData.layout) ? sectionData.layout : {};
  const content = isPlainObject(sectionData.content) ? sectionData.content : {};
  const style = isPlainObject(sectionData.style) ? sectionData.style : {};
  if (layout.columns !== 2) return false;
  if (style.backgroundImage != null) return false;
  const images = Array.isArray(content.images) ? content.images : [];
  return images.length === 1;
}

export default buildFamilyCanvas;
